import {
  EXCEL_DATA_FAILURE,
  EXCEL_DATA_LOADING,
  EXCEL_DATA_SUCCESS,
} from './constants';
import { createReducer } from './utils';
import { dispatchSnack } from './utils/snackbar';

const defaultState = {
  loading: false,
  errorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [EXCEL_DATA_LOADING]: handleLoading,
  [EXCEL_DATA_SUCCESS]: handleSuccess,
  [EXCEL_DATA_FAILURE]: handleFailure,
});

function handleLoading(state) {
  return { ...state, loading: true, errorMessage: null };
}

function handleSuccess(state) {
  return { ...state, loading: false };
}

function handleFailure(state, { payload: { error } }) {
  return { ...state, loading: false, errorMessage: error };
}

// actions

export function downloadExcel(task_type) {
  return async (dispatch, getState, { services: { dataSource, colors } }) => {
    dispatch({ type: EXCEL_DATA_LOADING });

    const { stores: { currentStore } } = getState();

    // Validate currentStore exists
    if (!currentStore) {
      dispatch({ type: EXCEL_DATA_FAILURE, payload: { error: 'No store selected' } });
      dispatchSnack(dispatch, 'bruno.excel.download_error', colors.getErrorColor());
      return { success: false };
    }

    const lang = localStorage.getItem('i18nextLng');
    const language = lang ? lang.split('-')[0] : null;

    try {
      const blob = await dataSource.requestExcelDownload({ store: currentStore, task_type, language });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Sanitize store name for filename
      const sanitizedStore = currentStore.replace(/[/\\:*?"<>|]/g, '_');
      const date = new Date().toISOString().slice(0, 10);
      a.download = `${sanitizedStore}_tasks_${task_type}_${date}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL after download completes
      a.addEventListener('load', () => window.URL.revokeObjectURL(url), { once: true });
      // Fallback: revoke after 5 seconds if load event doesn't fire
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);

      dispatch({ type: EXCEL_DATA_SUCCESS });
      dispatchSnack(dispatch, 'bruno.excel.download_success', colors.getSuccessColor());
      return { success: true };
    } catch (error) {
      dispatch({ type: EXCEL_DATA_FAILURE, payload: { error: error.message } });
      dispatchSnack(dispatch, 'bruno.excel.download_error', colors.getErrorColor());
      return { success: false };
    }
  };
}

// `session` is the session reported by previewExcel. Sending it back pins the
// write to the session the user actually saw counts for, instead of whichever
// session is active by the time they confirm. Omit it for one-shot uploads.
//
// `searchExcel` selects the file format, and the backend rejects a file sent
// under the wrong one. It defaults to false — the filled-in task download, the
// same format previewExcel asks the backend to parse — so a preview and the
// confirm that follows it can never disagree on how the file is read. The
// products "report tasks" upload (bare product keys, no header) passes true.
export function uploadExcel({ task_type, file, searchExcel = false, session }) {
  return async (dispatch, getState, { services: { dataSource, colors } }) => {
    dispatch({ type: EXCEL_DATA_LOADING });

    const { stores: { currentStore } } = getState();

    if (!currentStore) {
      dispatch({ type: EXCEL_DATA_FAILURE, payload: { error: 'No store selected' } });
      dispatchSnack(dispatch, 'bruno.excel.upload_error', colors.getErrorColor());
      return { success: false };
    }

    try {
      const response = await dataSource.requestExcelUpload({
        store: currentStore,
        task_type,
        file,
        search_excel: searchExcel,
        session,
      });
      if (response?.status >= 400) throw new Error(response.message);
      dispatch({ type: EXCEL_DATA_SUCCESS });
      dispatchSnack(dispatch, 'bruno.excel.upload_success', colors.getSuccessColor());
      return { success: true };
    } catch (error) {
      dispatch({ type: EXCEL_DATA_FAILURE, payload: { error: error.message } });
      dispatchSnack(dispatch, 'bruno.excel.upload_error', colors.getErrorColor());
      return { success: false };
    }
  };
}

// Dry-run preview: how many tasks would be created vs updated. No DB writes,
// no success snackbar — it only feeds the confirmation summary in the UI.
export function previewExcel({ task_type, file }) {
  return async (dispatch, getState, { services: { dataSource, colors } }) => {
    const { stores: { currentStore } } = getState();

    if (!currentStore) {
      dispatchSnack(dispatch, 'bruno.excel.upload_error', colors.getErrorColor());
      return { success: false };
    }

    try {
      const response = await dataSource.requestExcelUpload({
        store: currentStore,
        task_type,
        file,
        dry_run: true,
      });
      if (response?.status >= 400) throw new Error(response.message);
      // A backend that predates dry_run support ignores the flag and RUNS the
      // upload, answering without this marker. Refusing to show a summary keeps
      // the user from confirming — and applying the file a second time.
      if (response?.dry_run !== true) {
        throw new Error('Backend did not honor dry_run; preview is unavailable.');
      }
      return {
        success: true,
        willCreate: response.will_create ?? 0,
        willUpdate: response.will_update ?? 0,
        invalid: response.invalid ?? 0,
        // Session these counts describe — hand it to uploadExcel on confirm.
        session: response.session ?? null,
      };
    } catch (error) {
      dispatchSnack(dispatch, 'bruno.excel.preview_error', colors.getErrorColor());
      return { success: false };
    }
  };
}
